import { SpinnerLoader } from '@assets/icons'
import { ICards } from '@interfaces/home/ICards'
import INewUser from '@interfaces/home/INewUser'
import IShareData from '@interfaces/home/IShareData'
import { USER_STATUS } from '@interfaces/userManagement/enums/userStatus'
import { errorHandler } from '@services/index'
import DashboardService from '@services/requests/dashboardService'
import { format } from 'date-fns'
import ptBR from 'date-fns/locale/pt-BR'
import React from 'react'
import { toast } from 'react-toastify'
import { AreaChart, Area, XAxis, CartesianGrid, Tooltip, ResponsiveContainer, YAxis } from 'recharts'

export default function Home(): React.ReactElement {

    const [isLoading, setIsLoading] = React.useState(false)
    const [isLoadingCustomers, setIsLoadingCustomers] = React.useState(false)
    const [isLoadingRealtors, setIsLoadingRealtors] = React.useState(false)
    const [cards, setCards] = React.useState<ICards>({
        totalClients: 0,
        totalRealtors: 0,
        totalReleases: 0,
        totalSelected: 0
    })
    const [shareData, setShareData] = React.useState<IShareData[]>([])
    const [newRealtors, setNewRealtors] = React.useState<INewUser[]>([])
    const [newCustomers, setNewCustomers] = React.useState<INewUser[]>([])

    // Defini o status em português
    const getStatus = (status: USER_STATUS): string => {
        switch (status) {
            case 'AWAITING':
                return 'Aprovação'
            case 'ACTIVE':
                return 'Ativo'
            case 'INACTIVE':
                return 'Inativo'
            default:
                return status
        }
    }

    React.useEffect(() => {
        getCards()
        getShareData()
        getNewRealtors()
        getNewCustomers()
    }, [])

    async function getCards(): Promise<void> {
        setIsLoading(true)
        try {
            const response: ICards = await DashboardService.getCards()
            setCards(response)
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        }
        setIsLoading(false)
    }

    async function getShareData(): Promise<void> {
        setIsLoading(true)
        try {
            const response = await DashboardService.getShareData()
            setShareData(formatDateChart(response))
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        }
        setIsLoading(false)
    }

    function formatDateChart(data: IShareData[]): Array<{ date: string; sharing: number }> {
        const dateFormatted = data.map((item) => {
            const parts = item.date.split('-')
            const formattedDate = `${parts[1]}/${parts[2]}/${parts[0]}`
            const dateObject = new Date(formattedDate)
            return {
                date: format(new Date(dateObject), 'MMM dd', { locale: ptBR }),
                sharing: item.sharing,
            }
        })

        return dateFormatted
    }

    function formatDateList(createdAt: string): string {
        return format(new Date(createdAt), 'dd/MM', { locale: ptBR })
    }

    function formattedName(name: string, maxLength = 40): string {
        return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name
    }

    async function getNewCustomers(): Promise<void> {
        setIsLoadingCustomers(true)
        try {
            const response = await DashboardService.getNewCustomers()
            setNewCustomers(response)
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        }
        setIsLoadingCustomers(false)
    }

    async function getNewRealtors(): Promise<void> {
        setIsLoadingRealtors(true)
        try {
            const response = await DashboardService.getNewRealtors()
            setNewRealtors(response)
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        }
        setIsLoadingRealtors(false)
    }

    return (
        <div className="card">
            <div className="home">
                {isLoading ? (
                    <div className="loader-page">
                        <SpinnerLoader />
                    </div>
                ) : (
                    <></>
                )}
                <div className='wrapper-cards'>
                    <div className='card'>
                        <p>Total de Clientes</p>
                        <span>{cards.totalClients}</span>
                    </div>
                    <div className='card'>
                        <p>Total de Corretores</p>
                        <span>{cards.totalRealtors}</span>
                    </div>
                    <div className='card'>
                        <p>Total de Lançamentos</p>
                        <span>{cards.totalReleases}</span>
                    </div>
                    <div className='card'>
                        <p>Total de Selecionados</p>
                        <span>{cards.totalSelected}</span>
                    </div>
                </div>
                <div className='wrapper-graphic'>
                    <h5>Compartilhamento dos últimos 30 dias</h5>
                    <ResponsiveContainer width="100%" height={350}>
                        <AreaChart
                            className='graphic-sharing'
                            width={600}
                            height={500}
                            data={shareData}
                            margin={{
                                top: 10,
                                right: 40,
                                left: 0,
                                bottom: 0
                            }}
                        >
                            <defs>
                                <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="30%" stopColor="#a69478" stopOpacity={0.9} />
                                    <stop offset="70%" stopColor="#a69478" stopOpacity={0.2} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 1" />
                            <YAxis allowDataOverflow />
                            <XAxis dataKey="date" allowDataOverflow />
                            <Tooltip />
                            <Area type="monotone" dataKey="sharing" name='Compartilhamento(s)' stroke="#a69478" fill="url(#colorUv)" strokeWidth={3} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
                <div className='lists'>
                    <div className='list-customers'>
                        <h5>Últimos Clientes</h5>
                        <table>
                            <thead>
                                <tr>
                                    <th className='wrap-date'>
                                        Data
                                    </th>
                                    <th className='wrap-date'>
                                        Nome
                                    </th>
                                    <th className='wrap-date'>
                                        Status
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoadingCustomers ? (
                                    <div className="loader-page">
                                        <SpinnerLoader />
                                    </div>
                                ) : (
                                    <>
                                        {newCustomers.map((customers) => {
                                            return (
                                                <tr key={customers.name} className='wrapper-customers'>
                                                    <td className='wrap-date'>
                                                        {formatDateList(customers.createdAt)}
                                                    </td>
                                                    <td className='wrap-name' title={customers.name}>
                                                        {formattedName(customers.name)}
                                                    </td>
                                                    <td className='wrap-status'>
                                                        {getStatus(customers.status)}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className='list-realtors'>
                        <h5>Últimos Corretores</h5>
                        <table>
                            <thead>
                                <tr>
                                    <th className='wrap-date'>
                                        Data
                                    </th>
                                    <th className='wrap-date'>
                                        Nome
                                    </th>
                                    <th className='wrap-date'>
                                        Status
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoadingRealtors ? (
                                    <div className="loader-page">
                                        <SpinnerLoader />
                                    </div>
                                ) : (
                                    <>
                                        {newRealtors.map((realtors) => {
                                            return (
                                                <tr key={realtors.name} className='wrapper-realtors'>
                                                    <td className='wrap-date'>
                                                        {formatDateList(realtors.createdAt)}
                                                    </td>
                                                    <td className='wrap-name' title={realtors.name}>
                                                        {formattedName(realtors.name)}
                                                    </td>
                                                    <td className='wrap-status'>
                                                        {getStatus(realtors.status)}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    )
}
